import axios from 'axios';
import logger from '../utils/logger';
// Written by the branded login (TenantLogin) — the workspace the user came in
// through. Declared alongside the other session-scoped keys so that sign-out has
// a single place to clear them all.
import { WORKSPACE_SLUG_KEY } from '../config/sessionKeys';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const ACCESS_TOKEN_KEY = 'accessToken';
const RETRYABLE_STATUS_CODES = new Set([408, 425, 429, 500, 502, 503, 504]);
// What the auth middleware answers when it could not fetch its signing keys.
const AUTH_UNAVAILABLE_DETAIL = 'Authentication service temporarily unavailable';
const AUTH_UNAVAILABLE_RETRIES = 2;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export class ApiError extends Error {
    constructor(message, { status = null, code = null, details = null, originalError = null } = {}) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.details = details;
        this.originalError = originalError;
    }
}

// Dev-only identity switch (docs/decisions/role-presets.md): with auth
// disabled, a local QA rig can act as another existing local user by
// setting this localStorage key. Never sent unless REACT_APP_DISABLE_AUTH
// is "true" (the backend honours it only with DISABLE_AUTH as well).
export const DEV_USER_KEY = 'ironsight.devUser';

/** The X-Dev-User value to send, or null (always null with auth enabled). */
export const devUserHeader = () => {
    if (process.env.REACT_APP_DISABLE_AUTH !== 'true') return null;
    try {
        const v = localStorage.getItem(DEV_USER_KEY);
        return v && v.trim() ? v.trim() : null;
    } catch (e) {
        return null;
    }
};

export const isApiAbortError = (error) => (
    error?.code === 'ERR_CANCELED'
    || error?.name === 'CanceledError'
    || error?.message === 'canceled'
);

class ApiService {
    constructor() {
        this.accessToken = null;
        // Supplied by AuthContext. Entra access tokens last 60–90 minutes and nothing
        // renewed them, so every request after that simply 401'd — and because no code
        // looked at 401 either, the app kept rendering: an empty task board and an
        // endless "Analysing…" were indistinguishable from having no data.
        this.refreshAccessToken = null;
        // One refresh in flight at a time, shared by every request that 401s together.
        this._refreshInFlight = null;

        this.client = axios.create({
            baseURL: API_BASE_URL,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        this.client.interceptors.request.use(
            (config) => {
                const token = this.accessToken || sessionStorage.getItem(ACCESS_TOKEN_KEY);
                if (token) {
                    config.headers = config.headers || {};
                    config.headers.Authorization = `Bearer ${token}`;
                }
                // The workspace the user signed in through. Only meaningful for staff
                // who span several dealerships — for everyone else the backend derives
                // the tenant from the identity and ignores this. It is a preference,
                // never an authorisation: the backend validates it against the caller's
                // own memberships and rejects anything else.
                const workspace = sessionStorage.getItem(WORKSPACE_SLUG_KEY);
                if (workspace) {
                    config.headers = config.headers || {};
                    config.headers['X-Tenant-Slug'] = workspace;
                }
                const devUser = devUserHeader();
                if (devUser) {
                    config.headers = config.headers || {};
                    config.headers['X-Dev-User'] = devUser;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // A 403 on a tenant-data route means PricePath access was revoked/suspended
        // (require_pricepath). Signal the app to show the access screen instead of a
        // confusing data error. Admin-route 403s are action-level and left untouched.
        this.client.interceptors.response.use(
            (response) => response,
            (error) => {
                const status = error?.response?.status;
                const url = error?.config?.url || '';
                if (status === 403 && !url.startsWith('/api/admin')) {
                    window.dispatchEvent(new CustomEvent('pp:access-revoked'));
                }
                return Promise.reject(error);
            }
        );
    }

    /**
     * Register the callback that renews an expired access token.
     *
     * AuthContext already had `getAccessToken` — silent renewal with an interactive
     * fallback — but nothing ever called it. Handing it over here is what turns a
     * 401 from a dead end into a retry.
     */
    setTokenRefresher(refresh) {
        this.refreshAccessToken = refresh;
    }

    /**
     * Force a genuinely new token, collapsing concurrent callers onto one attempt.
     *
     * Resolves to `{ token }` on success, or `{ token: null, interactionRequired }`
     * when renewal failed — `interactionRequired` meaning only the user can fix it by
     * signing in again, as opposed to a transient failure that must not end a session.
     */
    async _refreshOnce() {
        if (!this.refreshAccessToken) return { token: null, interactionRequired: false };
        if (!this._refreshInFlight) {
            this._refreshInFlight = Promise.resolve()
                // forceRefresh: MSAL otherwise returns the cached token the API has
                // just refused, and the replay 401s exactly like the original.
                .then(() => this.refreshAccessToken({ forceRefresh: true }))
                .then((token) => ({ token: token || null, interactionRequired: false }))
                .catch((err) => {
                    logger.error('Token refresh failed', err);
                    return { token: null, interactionRequired: Boolean(err?.interactionRequired) };
                })
                .finally(() => { this._refreshInFlight = null; });
        }
        return this._refreshInFlight;
    }

    /** The token this request was sent with, if any. */
    _sentToken(error) {
        const header = error?.config?.headers?.Authorization || error?.config?.headers?.authorization;
        return typeof header === 'string' && header.startsWith('Bearer ') ? header.slice(7) : null;
    }

    /** A 503 from the auth middleware: the token was not checked, not rejected. */
    _isAuthUnavailable(error) {
        return error?.response?.status === 503
            && error?.response?.data?.detail === AUTH_UNAVAILABLE_DETAIL;
    }

    setAccessToken(token) {
        this.accessToken = token;
        if (token) {
            sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
        } else {
            sessionStorage.removeItem(ACCESS_TOKEN_KEY);
        }
    }

    normalizeError(error, fallbackMessage) {
        if (isApiAbortError(error)) return error;

        const status = error?.response?.status || null;
        const payload = error?.response?.data || null;
        const message = payload?.message || error?.message || fallbackMessage;
        const code = payload?.code || error?.code || null;

        return new ApiError(message, {
            status,
            code,
            details: payload,
            originalError: error,
        });
    }

    shouldRetry(error) {
        if (!error) return false;
        if (isApiAbortError(error)) return false;
        if (error.code === 'ECONNABORTED') return true;

        const status = error?.response?.status;
        return status ? RETRYABLE_STATUS_CODES.has(status) : true;
    }

    async request(config, options = {}) {
        const {
            signal,
            retry = (config.method || 'get').toLowerCase() === 'get' ? 1 : 0,
            retryDelayMs = 250,
            authRetryDelayMs = 500,
        } = options;

        let attempt = 0;
        let authAttempt = 0;
        let triedRefresh = false;
        while (true) {
            try {
                return await this.client.request({
                    ...config,
                    signal,
                });
            } catch (error) {
                if (isApiAbortError(error)) {
                    throw error;
                }

                // An expired token is worth exactly one renewal and one replay, on any
                // verb: the request never reached a handler, so replaying it cannot
                // duplicate a write. Tried before shouldRetry so it is not confused
                // with a transient 5xx, and only once.
                //
                // Only a renewal that needs the user (interaction_required and the
                // like) ends the session. A 401 on its own does not: it can be a
                // backend hiccup or a token replaced mid-flight, and treating it as
                // expiry is what used to sign working users out.
                if (error?.response?.status === 401 && !triedRefresh) {
                    triedRefresh = true;
                    const sent = this._sentToken(error);
                    const current = this.accessToken || sessionStorage.getItem(ACCESS_TOKEN_KEY);
                    // Another request already renewed the token this one was sent with.
                    if (sent && current && sent !== current) continue;

                    const { token, interactionRequired } = await this._refreshOnce();
                    if (token) continue;
                    if (interactionRequired) {
                        window.dispatchEvent(new CustomEvent('pp:session-expired'));
                        throw this.normalizeError(error, 'Your session has expired. Please sign in again.');
                    }
                    throw this.normalizeError(error, `Request failed for ${config.url}`);
                }

                // The backend could not check the token (its signing keys were out of
                // reach). Nothing ran, so any verb may be replayed; back off briefly.
                if (this._isAuthUnavailable(error)) {
                    if (authAttempt < AUTH_UNAVAILABLE_RETRIES) {
                        authAttempt += 1;
                        await wait(authRetryDelayMs * 2 ** (authAttempt - 1));
                        continue;
                    }
                    throw this.normalizeError(error, 'Sign-in service is temporarily unavailable. Please try again.');
                }

                if (attempt < retry && this.shouldRetry(error)) {
                    attempt += 1;
                    await wait(retryDelayMs * attempt);
                    continue;
                }

                throw this.normalizeError(error, `Request failed for ${config.url}`);
            }
        }
    }

    async getInventory(options = {}) {
        const response = await this.request({ method: 'get', url: '/api/inventory' }, options);
        return response.data;
    }

    // Committed units (sale pending, pre-sold, sold-not-closed) for the
    // "Include committed units" toggle: { units, count }. Never part of the
    // regular inventory's KPIs.
    async getCommittedInventory(options = {}) {
        const response = await this.request({ method: 'get', url: '/api/inventory/committed' }, options);
        return response.data;
    }

    async clearCache(options = {}) {
        const response = await this.request({ method: 'post', url: '/api/inventory/cache/clear' }, options);
        return response.data;
    }

    async getCacheStatus(options = {}) {
        const response = await this.request({ method: 'get', url: '/api/inventory/cache/status' }, options);
        return response.data;
    }

    async getPredictionHistory(stockNumber, options = {}) {
        const response = await this.request({
            method: 'get',
            url: `/api/inventory/${stockNumber}/prediction-history`,
        }, options);
        return response.data;
    }

    async getUnitPriceHistory(stockNumber, options = {}) {
        // Deliberately does NOT swallow errors into a default shape (unlike the
        // pipeline / service-history getters): the Pricing tab distinguishes a
        // genuine load failure (inline error) from a unit that simply has no
        // recorded price changes (empty state), so the error must propagate to
        // the caller's .catch.
        const response = await this.request({
            method: 'get',
            url: `/api/inventory/${stockNumber}/price-history`,
        }, options);
        return response.data;
    }

    async getUnitPipeline(stockNumber, options = {}) {
        try {
            const response = await this.request({ method: 'get', url: `/api/inventory/${stockNumber}/pipeline` }, options);
            return response.data;
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            logger.error(`Failed to get pipeline for ${stockNumber}`, error);
            return { stock_number: stockNumber, open: [], lost: [] };
        }
    }

    async getUnitMachinefinderData(stockNumber, options = {}) {
        try {
            const response = await this.request({ method: 'get', url: `/api/inventory/${stockNumber}/machinefinder` }, options);
            return response.data.found ? response.data : null;
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            logger.error(`Failed to get MachineFinder data for ${stockNumber}`, error);
            return null;
        }
    }

    async getUnitImages(stockNumber, options = {}) {
        try {
            const response = await this.request({ method: 'get', url: `/api/inventory/${stockNumber}/images` }, options);
            return response.data.images || [];
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            logger.error(`Failed to get images for ${stockNumber}`, error);
            return [];
        }
    }

    async getUnitQuoteEquipment(stockNumber, options = {}) {
        try {
            const response = await this.request({ method: 'get', url: `/api/inventory/${stockNumber}/quote-equipment` }, options);
            return response.data;
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            logger.error(`Failed to get quote equipment for ${stockNumber}`, error);
            return { stock_number: stockNumber, quotes: [], total_quotes: 0 };
        }
    }

    async getUnitServiceHistory(stockNumber, options = {}) {
        try {
            const response = await this.request({ method: 'get', url: `/api/inventory/${stockNumber}/service-history` }, options);
            return response.data;
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            logger.error(`Failed to get service history for ${stockNumber}`, error);
            return { stock_number: stockNumber, items: [], total_items: 0, work_order_count: 0 };
        }
    }

    async getEnrichedServiceHistory(stockNumber, options = {}) {
        try {
            const params = options.forceRefresh ? '?force_refresh=true' : '';
            const response = await this.request({ method: 'get', url: `/api/inventory/${stockNumber}/service-history/enriched${params}` }, options);
            return response.data;
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            logger.error(`Failed to get enriched service history for ${stockNumber}`, error);
            return { stock_number: stockNumber, work_orders: [], total_ros: 0, total_parts: 0 };
        }
    }

    async askServiceHistory(stockNumber, { question, summary, history = [], unitMeta = {}, serviceHistory = null }, options = {}) {
        const response = await this.request(
            {
                method: 'post',
                url: `/api/inventory/${stockNumber}/service-history/ask`,
                data: { question, summary, history, unit_meta: unitMeta, service_history: serviceHistory },
            },
            options,
        );
        return response.data;
    }

    async getServiceHistorySummary(stockNumber, unitMeta = {}, options = {}) {
        const params = new URLSearchParams();
        const fields = [
            'make', 'model', 'category', 'year', 'hours', 'price', 'days_in_stock', 'new_used',
            'ml_predicted_price', 'predicted_price',
            'days_to_sell', 'predicted_bucket', 'bucket_confidence',
            'internal_price', 'gross_margin_pct', 'margin_after_carrying_pct',
        ];
        fields.forEach(f => { if (unitMeta[f] != null) params.set(f, unitMeta[f]); });
        // Array fields — append each value separately
        if (Array.isArray(unitMeta.mf_options) && unitMeta.mf_options.length > 0) {
            unitMeta.mf_options.forEach(opt => params.append('mf_options', opt));
        }
        const query = params.toString();
        const url = `/api/inventory/${stockNumber}/service-history/summary${query ? `?${query}` : ''}`;
        const response = await this.request({ method: 'get', url }, options);
        return response.data;
    }

    async getUnitFastTrackAgent(stockNumber, unitMeta = {}, options = {}) {
        const { forceRefresh = false, signal, ...restOptions } = options;
        const params = new URLSearchParams();

        // Map unit object fields to snake_case query params
        const fieldMap = {
            make: unitMeta.make,
            model: unitMeta.model,
            category: unitMeta.category,
            year: unitMeta.year,
            hours: unitMeta.hours,
            price: unitMeta.price,
            days_in_stock: unitMeta.days_in_stock,
            predicted_price: unitMeta.predicted_price,
            days_to_sell: unitMeta.days_to_sell,
            laden_risk: unitMeta.LadenhueterRisk,
            risk_tier: unitMeta.RiskTier,
            quote_count: unitMeta.quote_count,
            days_since_last_quote: unitMeta.days_since_last_quote,
            gross_margin: unitMeta.gross_margin,
            gross_margin_pct: unitMeta.gross_margin_pct,
            carrying_cost_daily: unitMeta.carrying_cost_daily,
            carrying_cost_to_date: unitMeta.carrying_cost_to_date,
            margin_after_carrying_pct: unitMeta.margin_after_carrying_pct,
            margin_at_sale_pct: unitMeta.margin_at_sale_pct,
            mmy_median_asking_price: unitMeta.mmy_median_asking_price,
            mmy_median_days_in_stock: unitMeta.mmy_median_days_in_stock,
        };

        Object.entries(fieldMap).forEach(([key, val]) => {
            if (val != null) params.set(key, val);
        });

        if (forceRefresh) params.set('force_refresh', 'true');

        const query = params.toString();
        const url = `/api/inventory/${stockNumber}/fasttrack-agent${query ? `?${query}` : ''}`;

        try {
            // No automatic retry: a failed brief is an expensive LLM generation, and a
            // silent replay doubled every failure. The pane offers "Try again" instead.
            const response = await this.request({ method: 'get', url }, { signal, retry: 0, ...restOptions });
            return response.data;
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            logger.error(`Failed to get FastTrack agent for ${stockNumber}`, error);
            return null;
        }
    }

    async getUnitTractorHouse(stockNumber, options = {}) {
        try {
            const response = await this.request({
                method: 'get',
                url: `/api/inventory/${stockNumber}/tractorhouse`,
            }, options);
            return response.data;
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            // 404 = no TractorHouse listing for this unit — not an error
            if (error?.status === 404) return null;
            logger.error(`Failed to get TractorHouse data for ${stockNumber}`, error);
            return null;
        }
    }

    async getUnitGA4(stockNumber, options = {}) {
        try {
            const response = await this.request({
                method: 'get',
                url: `/api/inventory/${stockNumber}/ga4`,
            }, options);
            return response.data;
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            if (error?.status === 404) return null;
            logger.error(`Failed to get GA4 for ${stockNumber}`, error);
            return null;
        }
    }

    async getUnitComps(stockNumber, make, model, year, options = {}) {
        try {
            const params = new URLSearchParams();
            if (make) params.set('make', make);
            if (model) params.set('model', model);
            if (year) params.set('year', year);

            const response = await this.request({
                method: 'get',
                url: `/api/inventory/${stockNumber}/comps?${params.toString()}`,
            }, options);
            return response.data;
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            logger.error(`Failed to get comps for ${stockNumber}`, error);
            return { stock_number: stockNumber, active_peers: [], sold_comps: [] };
        }
    }

    async getMarketComps(stockNumber, options = {}) {
        // Deliberate deviation from the usual "swallow errors, return an empty
        // shape" convention above: the Comps tab must distinguish "market data
        // unavailable" (network/HTTP failure -> null) from "no market coverage
        // for this unit" (a well-formed response with available=false).
        try {
            const response = await this.request({
                method: 'get',
                url: `/api/inventory/${stockNumber}/market-comps`,
            }, options);
            return response.data;
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            logger.error(`Failed to get market comps for ${stockNumber}`, error);
            return null;
        }
    }

    // One market source of the Comps tab (sold | auction | advertised). Unlike
    // getMarketComps this one THROWS on failure: each source card shows its
    // own error with a Retry, so "failed" must stay distinguishable from a
    // well-formed empty / not_covered answer. A cold build can take up to a
    // minute server-side, so the timeout is generous (and on the axios config:
    // request() does not forward a timeout given in its options). No automatic
    // retry: the backend already retries the market feed once, and the card
    // has its own Retry.
    async getMarketSource(stockNumber, source, options = {}) {
        const response = await this.request({
            method: 'get',
            url: `/api/inventory/${encodeURIComponent(stockNumber)}/market-comps/sources/${source}`,
            timeout: 120000,
        }, { retry: 0, ...options });
        return response.data;
    }

    // The dealer's own sold units of the model (Comps tab overlay). Throws on
    // failure, like getMarketSource.
    async getOwnSales(stockNumber, options = {}) {
        const response = await this.request({
            method: 'get',
            url: `/api/inventory/${encodeURIComponent(stockNumber)}/market-comps/own-sales`,
            timeout: 60000,
        }, { retry: 0, ...options });
        return response.data;
    }

    async getMarketCompsPhoto(stockNumber, listingIndex, photoIndex, buildId, options = {}) {
        // Market-listing photos come through our backend image proxy, which
        // requires the Bearer token — a plain <img src> cannot send it. So the
        // photo is fetched as a Blob through the authenticated client and the
        // caller renders it via URL.createObjectURL (and revokes it when done).
        // A failed photo answers null: the gallery shows a muted per-photo
        // fallback instead of failing the whole panel.
        //
        // buildId is the payload's build_id: the proxy only resolves indexes
        // against the image map written by that same build, so a payload that
        // outlived the server-side cache can never show the wrong machine's
        // photos — the stale build 404s into the muted fallback instead.
        try {
            const response = await this.request({
                method: 'get',
                url: `/api/inventory/${stockNumber}/market-comps/photos/${listingIndex}/${photoIndex}`
                    + `?build=${encodeURIComponent(buildId ?? '')}`,
                responseType: 'blob',
            }, options);
            return response.data;
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            logger.error(`Failed to get market photo ${listingIndex}/${photoIndex} for ${stockNumber}`, error);
            return null;
        }
    }

    async getBranchHealth(options = {}) {
        const response = await this.request({ method: 'get', url: '/api/branches/health' }, options);
        return response.data;
    }

    async getPricingMatrix(options = {}) {
        const response = await this.request({ method: 'get', url: '/api/branches/pricing-matrix' }, options);
        return response.data;
    }

    async getBranchInterest(options = {}) {
        const response = await this.request({ method: 'get', url: '/api/branches/buyer-interest' }, options);
        return response.data;
    }

    async getBranchCombined(options = {}) {
        const response = await this.request({ method: 'get', url: '/api/branches/combined' }, options);
        return response.data;
    }

    /** Per-unit used stock movements for the sales performance panel (docs/decisions/branch-compare.md). */
    async getBranchMovements(options = {}) {
        const response = await this.request({ method: 'get', url: '/api/branches/movements' }, options);
        return response.data;
    }

    async getUserPreferences(options = {}) {
        try {
            const response = await this.request({ method: 'get', url: '/api/user/preferences' }, options);
            return response.data;
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            // 404 = first-time user with no saved prefs — not an error condition
            if (error?.status === 404) return {};
            logger.error('Failed to load user preferences', error);
            return {};
        }
    }

    async saveUserPreferences(filters, anomalyChips, options = {}) {
        try {
            const response = await this.request(
                {
                    method: 'put',
                    url: '/api/user/preferences',
                    data: { filters, anomalyChips },
                },
                { ...options, retry: 0 },
            );
            return response.data;
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            logger.error('Failed to save user preferences', error);
            return null;
        }
    }

    async getTrendAnalysis(options = {}) {
        try {
            const response = await this.request({
                method: 'get',
                url: '/api/branches/category-trends/analysis',
            }, { ...options, timeout: 60000 });
            return response.data;
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            logger.error('Failed to get trend analysis', error);
            return null;
        }
    }

    async refreshTrendAnalysis(options = {}) {
        try {
            const response = await this.request({
                method: 'get',
                url: '/api/branches/category-trends/analysis?force_refresh=true',
            }, { ...options, timeout: 60000 });
            return response.data;
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            logger.error('Failed to refresh trend analysis', error);
            return null;
        }
    }

    async getCategoryTrends({ dateFrom, dateTo, ...options } = {}) {
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        const response = await this.request({ method: 'get', url: '/api/branches/category-trends', params }, options);
        return response.data;
    }

    // ── Activities ────────────────────────────────────────────────────────

    async createActivity(body) {
        const response = await this.request({ method: 'post', url: '/api/activities', data: body });
        return response.data;
    }

    async getActivities(params = {}) {
        const response = await this.request({ method: 'get', url: '/api/activities', params });
        return response.data;
    }

    async getUnitActivities(stockNumber) {
        const response = await this.request({ method: 'get', url: `/api/activities/unit/${stockNumber}` });
        return response.data;
    }

    async updateActivityStatus(activityId, body) {
        const response = await this.request({ method: 'put', url: `/api/activities/${activityId}/status`, data: body });
        return response.data;
    }

    async updateActivity(activityId, body) {
        const response = await this.request({ method: 'put', url: `/api/activities/${activityId}`, data: body });
        return response.data;
    }

    async deleteActivity(activityId) {
        const response = await this.request({ method: 'delete', url: `/api/activities/${activityId}` });
        return response.data;
    }

    async getActivityUsers() {
        const response = await this.request({ method: 'get', url: '/api/activities/users' });
        return response.data;
    }

    async getDirectoryUsers() {
        const response = await this.request({ method: 'get', url: '/api/activities/users/directory' }, { timeout: 30000 });
        return response.data;
    }

    /**
     * The dealerships this caller may look at.
     *
     * One entry with `read_only: false` for a dealer user; several, all
     * read-only, for SAG staff. Returns `[]` rather than throwing when the shape
     * is unfamiliar, so a caller can treat "no choice" and "could not ask" alike.
     */
    async getWorkspaces(options = {}) {
        const response = await this.request({ method: 'get', url: '/api/workspaces' }, options);
        const data = response.data;
        if (Array.isArray(data)) return data;
        return Array.isArray(data?.workspaces) ? data.workspaces : [];
    }

    // ── Admin / user management (sag_identity) ───────────────────────────
    async getMe(options = {}) {
        const response = await this.request({ method: 'get', url: '/api/admin/me' }, options);
        return response.data;
    }

    async getAdminOrgs(options = {}) {
        const response = await this.request({ method: 'get', url: '/api/admin/orgs' }, options);
        return response.data;
    }

    // Public (no auth) — resolve a workspace's name by slug for the branded login.
    async getPublicOrg(slug) {
        const base = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const res = await fetch(`${base}/api/admin/public/org/${encodeURIComponent(slug)}`);
        // The status rides on the error so the sign-in can tell "no such
        // workspace" (404) from "could not reach the server" (QA L-8).
        if (!res.ok) throw Object.assign(new Error(res.status === 404 ? 'Workspace not found' : `Workspace lookup failed (${res.status})`), { status: res.status });
        return res.json();
    }

    async getAdminUsers(orgId = null, options = {}) {
        const response = await this.request(
            { method: 'get', url: '/api/admin/users', params: orgId ? { org_id: orgId } : {} },
            options,
        );
        return response.data;
    }

    async createAdminOrg(payload, options = {}) {
        const response = await this.request(
            { method: 'post', url: '/api/admin/orgs', data: payload },
            options,
        );
        return response.data;
    }

    async onboardDealer(payload, options = {}) {
        const response = await this.request(
            { method: 'post', url: '/api/admin/onboard', data: payload },
            { timeout: 40000, ...options },
        );
        return response.data;
    }

    async searchDirectory(q, options = {}) {
        const response = await this.request(
            { method: 'get', url: '/api/admin/directory/search', params: { q } },
            { timeout: 20000, ...options },
        );
        return response.data;
    }

    async addAdminUser(payload, options = {}) {
        const response = await this.request(
            { method: 'post', url: '/api/admin/users', data: payload },
            options,
        );
        return response.data;
    }

    async inviteAdminUser(payload, options = {}) {
        const response = await this.request(
            { method: 'post', url: '/api/admin/users/invite', data: payload },
            { timeout: 30000, ...options },
        );
        return response.data;
    }

    async updateAdminUser(userId, payload, options = {}) {
        const response = await this.request(
            { method: 'patch', url: `/api/admin/users/${userId}`, data: payload },
            options,
        );
        return response.data;
    }

    async removeAdminUser(userId, options = {}) {
        const response = await this.request(
            { method: 'delete', url: `/api/admin/users/${userId}` },
            options,
        );
        return response.data;
    }

    async updateAdminOrg(orgId, payload, options = {}) {
        const response = await this.request(
            { method: 'patch', url: `/api/admin/orgs/${orgId}`, data: payload },
            options,
        );
        return response.data;
    }

    async deleteAdminOrg(orgId, options = {}) {
        const response = await this.request(
            { method: 'delete', url: `/api/admin/orgs/${orgId}` },
            options,
        );
        return response.data;
    }

    async moveAdminOrg(orgId, parentId, options = {}) {
        const response = await this.request(
            { method: 'post', url: `/api/admin/orgs/${orgId}/move`, data: { parent_id: parentId } },
            options,
        );
        return response.data;
    }

    async resetAdminUserPassword(userId, options = {}) {
        const response = await this.request(
            { method: 'post', url: `/api/admin/users/${userId}/reset-password` },
            { timeout: 30000, ...options },
        );
        return response.data;
    }

    // ── Dealership settings (docs/decisions/dealership-settings.md) ──────
    // The effective settings of the dealership in view; any signed-in user.
    async getSettings(options = {}) {
        const response = await this.request({ method: 'get', url: '/api/settings' }, options);
        return response.data;
    }

    // ── The signed-in user's own settings (column layout, …) ─────────────
    // { version, values, defaults, overridden, schema, can_save }.
    async getMySettings(options = {}) {
        const response = await this.request({ method: 'get', url: '/api/me/settings' }, options);
        return response.data;
    }

    // Optimistic concurrency like the dealership settings: a 409 means the
    // version moved on (another tab). `null` resets a key. Never retried.
    async saveMySettings(values, expectedVersion, options = {}) {
        const response = await this.request(
            { method: 'put', url: '/api/me/settings', data: { values, expected_version: expectedVersion } },
            { retry: 0, ...options },
        );
        return response.data;
    }

    async getAdminDealershipSettings(orgId, options = {}) {
        const response = await this.request(
            { method: 'get', url: `/api/admin/dealerships/${encodeURIComponent(orgId)}/settings` },
            options,
        );
        return response.data;
    }

    // Optimistic concurrency: a 409 means someone saved in the meantime.
    // Never retried, so a slow save is not sent twice.
    async saveAdminDealershipSettings(orgId, values, expectedVersion, options = {}) {
        const response = await this.request(
            {
                method: 'put',
                url: `/api/admin/dealerships/${encodeURIComponent(orgId)}/settings`,
                data: { values, expected_version: expectedVersion },
            },
            { retry: 0, ...options },
        );
        return response.data;
    }

    async resetAdminDealershipSetting(orgId, key, options = {}) {
        const response = await this.request(
            { method: 'delete', url: `/api/admin/dealerships/${encodeURIComponent(orgId)}/settings/${encodeURIComponent(key)}` },
            { retry: 0, ...options },
        );
        return response.data;
    }

    async getAdminDealershipSettingsHistory(orgId, limit = 20, options = {}) {
        const response = await this.request(
            {
                method: 'get',
                url: `/api/admin/dealerships/${encodeURIComponent(orgId)}/settings/history`,
                params: { limit },
            },
            options,
        );
        const data = response.data;
        if (Array.isArray(data)) return data;
        return Array.isArray(data?.history) ? data.history : (Array.isArray(data?.items) ? data.items : []);
    }

    // ── Role presets (docs/decisions/role-presets.md) ─────────────────────
    // Per dealership; the same access rule as the dealership settings.
    _presetsUrl(orgId, rest = '') {
        return `/api/admin/dealerships/${encodeURIComponent(orgId)}/presets${rest}`;
    }

    /** { items: [{ id, name, description, template_key, version, values, updated_at, updated_by, assigned, following, customized }], can_edit }. */
    async getAdminPresets(orgId, options = {}) {
        const response = await this.request({ method: 'get', url: this._presetsUrl(orgId) }, options);
        return response.data;
    }

    // { name, description, values?, copy_from? } → 201 with the preset; 409 on a duplicate name.
    async createAdminPreset(orgId, payload, options = {}) {
        const response = await this.request(
            { method: 'post', url: this._presetsUrl(orgId), data: payload },
            { retry: 0, ...options },
        );
        return response.data;
    }

    // Optimistic concurrency: 409 = changed meanwhile (or a duplicate name).
    async updateAdminPreset(orgId, presetId, payload, expectedVersion, options = {}) {
        const response = await this.request(
            {
                method: 'put',
                url: this._presetsUrl(orgId, `/${encodeURIComponent(presetId)}`),
                data: { ...payload, expected_version: expectedVersion },
            },
            { retry: 0, ...options },
        );
        return response.data;
    }

    // → { cleared_assignments, cleared_choices }.
    async deleteAdminPreset(orgId, presetId, expectedVersion, options = {}) {
        const response = await this.request(
            {
                method: 'delete',
                url: this._presetsUrl(orgId, `/${encodeURIComponent(presetId)}`),
                params: { expected_version: expectedVersion },
            },
            { retry: 0, ...options },
        );
        return response.data;
    }

    // Re-creates deleted seeded presets (never overwrites); → the re-created list.
    async restoreAdminDefaultPresets(orgId, options = {}) {
        const response = await this.request(
            { method: 'post', url: this._presetsUrl(orgId, '/restore-defaults') },
            { retry: 0, ...options },
        );
        return response.data;
    }

    async getAdminPresetHistory(orgId, presetId, limit = 20, options = {}) {
        const response = await this.request(
            { method: 'get', url: this._presetsUrl(orgId, `/${encodeURIComponent(presetId)}/history`), params: { limit } },
            options,
        );
        const data = response.data;
        if (Array.isArray(data)) return data;
        return Array.isArray(data?.items) ? data.items : (Array.isArray(data?.history) ? data.history : []);
    }

    // The admin's assignment for one member (null = system default).
    async setAdminMemberPreset(orgId, userId, presetId, options = {}) {
        const response = await this.request(
            {
                method: 'put',
                url: `/api/admin/dealerships/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}/preset`,
                data: { preset_id: presetId ?? null },
            },
            { retry: 0, ...options },
        );
        return response.data;
    }

    // ── Data sources / tenants (super-admin) ─────────────────────────────
    // A dealer's warehouse is built and registered by the data team. These routes
    // only record where it lives and prove that it answers — nothing is created here.

    async getAdminTenants(options = {}) {
        const response = await this.request({ method: 'get', url: '/api/admin/tenants' }, options);
        return response.data;
    }

    async connectAdminTenant(payload, options = {}) {
        const response = await this.request(
            { method: 'post', url: '/api/admin/tenants', data: payload, timeout: 30000 },
            { retry: 0, ...options },
        );
        return response.data;
    }

    // Opens a real connection to the dealer's Synapse endpoint and queries it, so this
    // is seconds — not milliseconds. Never retried: a slow probe must not be doubled up.
    async checkAdminTenant(tenantId, options = {}) {
        const response = await this.request(
            { method: 'post', url: `/api/admin/tenants/${tenantId}/check`, timeout: 180000 },
            { retry: 0, ...options },
        );
        return response.data;
    }

    // Re-probes the source server-side before switching the dealership live. Not
    // idempotent, so it is never retried — a 409 carries the failing steps in `detail`.
    async activateAdminTenant(tenantId, options = {}) {
        const response = await this.request(
            { method: 'post', url: `/api/admin/tenants/${tenantId}/activate`, timeout: 180000 },
            { retry: 0, ...options },
        );
        return response.data;
    }

    async removeAdminTenant(tenantId, options = {}) {
        const response = await this.request(
            { method: 'delete', url: `/api/admin/tenants/${tenantId}` },
            { retry: 0, ...options },
        );
        return response.data;
    }

    // ── 12-Month Forward Predictions ─────────────────────────────────────

    async getPredictions12m(stockNumber, options = {}) {
        try {
            const response = await this.request({
                method: 'get',
                url: `/api/predictions12m/${stockNumber}`,
            }, options);
            return response.data;
        } catch (error) {
            if (isApiAbortError(error)) throw error;
            // 404 = no 12-month forecast for this unit (e.g. pre-sold, not
            // yet received) — not an error
            if (error?.status === 404) return null;
            logger.error(`Failed to get 12m predictions for ${stockNumber}`, error);
            return null;
        }
    }
}

const apiService = new ApiService();
// eslint-disable-next-line import/no-anonymous-default-export
export default apiService;
